import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { MarqueeContainer } from './PhotoMarquee.styles';
import type { PhotoMarqueeProps } from './PhotoMarquee.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { LOW_RES_RENDITION, OptimizedEventGuestSitePhoto } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { addRendition } from '@shared/utils/photoRendition';
import { MAX_ASPECT_RATIO, PHOTO_HEIGHT, MAX_HEIGHT } from './PhotoMarquee.constants';
import { MarqueePhoto } from './MarqueePhoto';

const PHOTO_COUNT_TO_SHOW = 6;

export const PhotoMarquee: FC<PhotoMarqueeProps> = memo(props => {
  const { photos, showSkeleton, showPlaceholder } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);
  const marqueePhotos = useMemo(() => [...filledPhotos, ...filledPhotos], [filledPhotos]);

  const [containerDimensions, containerRef] = useSizeObserver();

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  return (
    <Flex
      width="100vw"
      position="relative"
      left="calc(-50vw + 50%)"
      overflow="hidden"
    >
      <MarqueeContainer
        flexDirection="row"
        flexBasis="100%"
        gap={1}
        maxHeight={MAX_HEIGHT}
        height={PHOTO_HEIGHT}
        ref={containerRef}
      >
        {marqueePhotos.map((photo, index) => (
          <SsrLazyScreen
            key={`${photo.id}-${index}`}
            src={addRendition({ url: photo.photoUrl, renditionSize: LOW_RES_RENDITION })}
            width={`calc(calc(100vw / ${MAX_ASPECT_RATIO}) * ${photo.width / photo.height})`}
            height="100%"
            showSkeleton={showSkeleton}
          >
            {() =>
              photo.photoUrl ? (
                <MarqueePhoto
                  photo={photo}
                  index={index}
                  containerDimensions={containerDimensions}
                  photoRenderer={OptimizedEventGuestSitePhoto}
                />
              ) : (
                <Box
                  width="100%"
                  height="100%"
                  aspectRatio={`${MAX_ASPECT_RATIO}`}
                  backgroundColor="lightgray"
                  key={photo.id}
                />
              )
            }
          </SsrLazyScreen>
        ))}
      </MarqueeContainer>
    </Flex>
  );
});

PhotoMarquee.displayName = 'PhotoMarquee';
